import { WatchLater } from '@mui/icons-material'
import { Button, IconButton, Stack, Typography } from '@mui/material'
import { TenantStore } from '@planning/rt-stores/tenant/TenantStore'
import { GateInViewStore } from '@planning/stores/gateControl/GateInViewStore'
import { useTranslate } from '@tolgee/react'
import { DateRangePicker, Header } from '@tom-ui/ui'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import { TruckVisitsViewStore } from '../../Stores/TruckVisitsViewStore'
import { TruckVisitClearFitlers } from '../Molecules/TruckVisitClearFilters'
import { TruckVisitSearch } from '../Molecules/TruckVisitSearch'
import { TruckOverdue } from './TruckOverdue'
import { TruckTimeRange } from './TruckTimeRange'
import { TruckVisitAdvancedFilters } from './TruckVisitAdvancedFilters'

interface Props {
  store: TruckVisitsViewStore
  gateInViewStore: GateInViewStore
  tenantStore: TenantStore
}
export const TruckVisitHeader = observer(({ store, gateInViewStore, tenantStore }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()

  const onTodayClick = () => {
    const todayStartOfDay = dayjs(new Date()).hour(0).minute(0).toDate()
    const todayEndOfDay = dayjs(new Date()).hour(23).minute(59).toDate()
    store.toggleCalendarPopper(false)
    store.setSelectedDateRange(todayStartOfDay, todayEndOfDay)
    store.clearTimeRange()
  }

  const onTimeRangeClick = () => {
    store.toggleTimeRangeDialog(true)
  }

  const onGateIn = async () => {
    gateInViewStore.setIsTruckAppointment(false)
    navigate('new')
  }

  const onCreateAppointment = async () => {
    gateInViewStore.setIsTruckAppointment(true)

    navigate('new')
  }

  return (
    <Header
      leftRenderOption={
        <Stack flex='none'>
          <Typography variant='h3'>{t('truckVisits', 'Truck visits')}</Typography>
          <TruckOverdue store={store} />
          <TruckTimeRange store={store} />
        </Stack>
      }
      rightRenderOption={
        <Stack flexDirection='row' alignItems='center' justifyContent='end' width='100%' gap={2}>
          <Button
            color='inherit'
            variant='contained'
            size='large'
            sx={{ flex: 'none' }}
            onClick={onTodayClick}
          >
            {t('today', 'Today')}
          </Button>

          <DateRangePicker
            startDate={store.selectedStartDate}
            endDate={store.selectedEndDate}
            onDateChange={(startDate, endDate) => store.setSelectedDateRange(startDate, endDate)}
            sx={{ height: '3.5rem' }}
          />

          <IconButton
            sx={{ border: `1px solid lightgray`, padding: '15px' }}
            color='secondary'
            onClick={onTimeRangeClick}
          >
            {store.isTimeRangeSet && (
              <Typography variant='body1' sx={{ marginRight: '5px' }}>
                {dayjs(store.timeRangeStart).format('HH:mm')}
                {' - '}
                {dayjs(store.timeRangeEnd).format('HH:mm')}
              </Typography>
            )}
            <WatchLater />
          </IconButton>

          <TruckVisitSearch store={store} />
          <TruckVisitAdvancedFilters store={store} />
          <TruckVisitClearFitlers store={store} />

          <Stack flexDirection='row' gap={2} flex='none'>
            {tenantStore.skipTruckAppointment && (
              <Button
                data-cy='gate-control-gate-in-btn'
                key='gateInButton'
                variant='contained'
                onClick={onGateIn}
              >
                {t('gateIn', 'Gate In')}
              </Button>
            )}

            {!tenantStore.skipTruckAppointment && (
              <Button
                key='createAppointment'
                variant='contained'
                onClick={onCreateAppointment}
                data-cy='create-appointment-btn'
              >
                {t('createVisitButton', 'Create visit')}
              </Button>
            )}
          </Stack>
        </Stack>
      }
    />
  )
})
